import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { AdminRoiService } from '../../../services/admin-roi.service';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { PrimengImports } from '../../../shared/primeng-imports';
import { ViewUserDetailsComponent } from '../view-user-details/view-user-details.component';

interface RoiRun {
  _id: string;
  monthKey: string;
  runAt: string;
  portfoliosProcessed: number;
  usersCredited: number;
  totalCredited: number;
}

interface RoiCreditDetail {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  portfolioId: string;
  portfolioName: string;
  portfolioRiskLevel: string;
  amount: number;
  creditDate: string;
  consumedAmount: number;
  monthKey: string;
}

interface PortfolioRoiStats {
  portfolioId: string;
  portfolioName: string;
  riskLevel: string;
  totalUsers: number;
  totalCredited: number;
  totalAvailable: number;
  lastCreditDate: string;
  actualRoiPercentage: number;
}

@Component({
  selector: 'app-roi-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideIconsModule,
    PrimengImports,
    ViewUserDetailsComponent
  ],
  templateUrl: './roi-dashboard.component.html',
  styleUrl: './roi-dashboard.component.css'
})
export class RoiDashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Loading states
  loading = {
    roiRuns: false,
    roiCredits: false,
    portfolioStats: false,
    triggering: false
  };

  // Data
  roiRuns: any[] = [];
  roiCredits: any[] = [];
  portfolioStats: any[] = [];

  // Filters and pagination
  selectedMonth: string = '';
  selectedPortfolio: string = '';
  searchUser: string = '';
  currentPage = 0;
  pageSize = 10;

  // Search debounce
  private searchSubject = new Subject<string>();

  // Summary stats
  summaryStats = {
    totalRoiRuns: 0,
    totalUsersCredited: 0,
    totalAmountCredited: 0,
    totalPortfoliosActive: 0,
    lastRunDate: '',
    averageRoiRate: 0
  };

  // Dialog states
  showUserDetailsDialog = false;
  selectedUserId: string | null = null;

  constructor(private adminRoiService: AdminRoiService) {}

  ngOnInit(): void {
    this.loadAllData();

    // Setup search debounce
    this.searchSubject
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.currentPage = 0;
        this.loadRoiCredits();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAllData(): void {
    this.loadRoiRuns();
    this.loadRoiCredits();
    this.loadPortfolioStats();
  }

  loadRoiRuns(): void {
    this.loading.roiRuns = true;
    this.adminRoiService.getRoiRuns()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.roiRuns = response.data;
            this.calculateSummaryStats();
          }
          this.loading.roiRuns = false;
        },
        error: (error) => {
          console.error('Error loading ROI runs:', error);
          this.loading.roiRuns = false;
        }
      });
  }

  loadRoiCredits(): void {
    this.loading.roiCredits = true;
    const params = {
      page: this.currentPage + 1,
      limit: this.pageSize,
      monthKey: this.selectedMonth,
      portfolioId: this.selectedPortfolio,
      search: this.searchUser
    };

    this.adminRoiService.getRoiCredits(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.roiCredits = response.data.credits;
          }
          this.loading.roiCredits = false;
        },
        error: (error) => {
          console.error('Error loading ROI credits:', error);
          this.loading.roiCredits = false;
        }
      });
  }

  loadPortfolioStats(): void {
    this.loading.portfolioStats = true;
    this.adminRoiService.getPortfolioRoiStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.portfolioStats = response.data;
            this.calculateSummaryStats();
          }
          this.loading.portfolioStats = false;
        },
        error: (error) => {
          console.error('Error loading portfolio ROI stats:', error);
          this.loading.portfolioStats = false;
        }
      });
  }

  triggerRoiJob(runDate?: string): void {
    this.loading.triggering = true;
    this.adminRoiService.triggerRoiJob(runDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.loadAllData(); // Refresh all data
          }
          this.loading.triggering = false;
        },
        error: (error) => {
          console.error('Error triggering ROI job:', error);
          this.loading.triggering = false;
        }
      });
  }

  calculateSummaryStats(): void {
    if (this.roiRuns.length > 0) {
      const latestRun = this.roiRuns[0];
      this.summaryStats = {
        totalRoiRuns: this.roiRuns.length,
        totalUsersCredited: this.roiRuns.reduce((sum, run) => sum + run.usersCredited, 0),
        totalAmountCredited: this.roiRuns.reduce((sum, run) => sum + run.totalCredited, 0),
        totalPortfoliosActive: this.portfolioStats.length,
        lastRunDate: latestRun?.runAt || '',
        averageRoiRate: this.portfolioStats.length > 0
          ? this.portfolioStats.reduce((sum, stat) => sum + stat.actualRoiPercentage, 0) / this.portfolioStats.length
          : 0
      };
    }
  }

  // Filter methods
  onMonthFilterChange(): void {
    this.currentPage = 0;
    this.loadRoiCredits();
  }

  onPortfolioFilterChange(): void {
    this.currentPage = 0;
    this.loadRoiCredits();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchUser);
  }

  onPageChange(event: any): void {
    this.currentPage = event.first / event.rows;
    this.loadRoiCredits();
  }

  // Dialog methods
  // User details dialog method
  viewUserDetails(userId: string): void {
    this.selectedUserId = userId;
    this.showUserDetailsDialog = true;
  }

  // Utility methods
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getRiskLevelClass(riskLevel: string): string {
    switch (riskLevel.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Paying': return 'bg-blue-100 text-blue-800';
      case 'Matured': return 'bg-purple-100 text-purple-800';
      case 'Upcoming': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getMonths(): string[] {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(date.toISOString().slice(0, 7));
    }
    return months;
  }

  getUniquePortfolios(): { id: string; name: string }[] {
    return this.portfolioStats.map(stat => ({
      id: stat.portfolioId,
      name: stat.portfolioName
    }));
  }
}
